import {
  createFrontendPlugin,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import AppsIcon from '@material-ui/icons/Apps';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import ViewListIcon from '@material-ui/icons/ViewList';
import PeopleIcon from '@material-ui/icons/People';
import FavoriteIcon from '@material-ui/icons/Favorite';

import { ANOTACION_PROYECTO } from './lib/gitlab';
import { ANOTACION_INFRA } from './lib/infra';
import {
  catalogoServiciosRouteRef,
  hojaDeVidaRouteRef,
  integracionesRouteRef,
  saludRouteRef,
  usuariosRouteRef,
} from './routes';

// Cada módulo del portal es una página; title + icon la agregan al menú.
// Para un módulo nuevo: agregar su routeRef, su PageBlueprint y listarlo abajo.

const hojaDeVidaPage = PageBlueprint.make({
  name: 'hoja-de-vida',
  params: {
    path: '/hoja-de-vida',
    title: 'Hoja de vida',
    icon: <AppsIcon />,
    routeRef: hojaDeVidaRouteRef,
    loader: () =>
      import('./components/HojaDeVidaPage').then(m => <m.HojaDeVidaPage />),
  },
});

const integracionesPage = PageBlueprint.make({
  name: 'integraciones',
  params: {
    path: '/integraciones',
    title: 'Integraciones',
    icon: <DeviceHubIcon />,
    routeRef: integracionesRouteRef,
    loader: () =>
      import('./components/IntegracionesPage').then(m => (
        <m.IntegracionesPage />
      )),
  },
});

const catalogoServiciosPage = PageBlueprint.make({
  name: 'catalogo-servicios',
  params: {
    path: '/catalogo-servicios',
    title: 'Catálogo de servicios',
    icon: <ViewListIcon />,
    routeRef: catalogoServiciosRouteRef,
    loader: () =>
      import('./components/CatalogoServiciosPage').then(m => (
        <m.CatalogoServiciosPage />
      )),
  },
});

/** Puntaje de infraestructura por producto (backend: vanti-infra GET /salud). */
const saludPage = PageBlueprint.make({
  name: 'salud',
  params: {
    path: '/salud',
    title: 'Salud',
    icon: <FavoriteIcon />,
    routeRef: saludRouteRef,
    loader: () => import('./components/SaludPage').then(m => <m.SaludPage />),
  },
});

/** Usuarios del portal y sus grupos (solo Admin; backend: vanti-usuarios). */
const usuariosPage = PageBlueprint.make({
  name: 'usuarios',
  params: {
    path: '/usuarios',
    title: 'Usuarios',
    icon: <PeopleIcon />,
    routeRef: usuariosRouteRef,
    loader: () =>
      import('./components/UsuariosPage').then(m => <m.UsuariosPage />),
  },
});

/** Ficha de hoja de vida en la página de cada aplicación (System). */
const hojaDeVidaCard = EntityCardBlueprint.make({
  name: 'hoja-de-vida',
  params: {
    filter: 'kind:system',
    type: 'info',
    loader: () =>
      import('./components/HojaDeVidaCard').then(m => <m.HojaDeVidaCard />),
  },
});

/** Pestaña "Hoja de vida" con todas las secciones, en cada System. */
const hojaDeVidaContent = EntityContentBlueprint.make({
  name: 'hoja-de-vida',
  params: {
    path: 'hoja-de-vida',
    title: 'Hoja de vida',
    filter: 'kind:system',
    loader: () =>
      import('./components/HojaDeVidaContent').then(m => (
        <m.HojaDeVidaContent />
      )),
  },
});

/**
 * Pestaña "Pipelines" en los componentes con proyecto de GitLab: pipelines,
 * jobs y logs sin salir del portal (backend: plugin vanti-gitlab).
 */
const pipelinesContent = EntityContentBlueprint.make({
  name: 'pipelines',
  params: {
    path: 'pipelines',
    title: 'Pipelines',
    filter: entity =>
      Boolean(entity.metadata.annotations?.[ANOTACION_PROYECTO]),
    loader: () =>
      import('./components/PipelinesContent').then(m => <m.PipelinesContent />),
  },
});

/**
 * Pestaña "Variables de entorno" en los System de producto (anotación
 * grupovanti.com/infra): variables de cada microservicio, sin mostrar
 * valores secretos (backend: vanti-infra).
 */
const variablesEntornoContent = EntityContentBlueprint.make({
  name: 'variables-entorno',
  params: {
    path: 'variables-entorno',
    title: 'Variables de entorno',
    filter: entity =>
      Boolean(entity.metadata.annotations?.[ANOTACION_INFRA]),
    loader: () =>
      import('./components/VariablesEntornoContent').then(m => (
        <m.VariablesEntornoContent />
      )),
  },
});

/**
 * Pestaña "Arquitectura" en los System de producto: AS-IS por ambiente desde
 * el tfstate, con hallazgos calculados (backend: vanti-infra).
 */
const arquitecturaContent = EntityContentBlueprint.make({
  name: 'arquitectura',
  params: {
    path: 'arquitectura',
    title: 'Arquitectura',
    filter: entity =>
      Boolean(entity.metadata.annotations?.[ANOTACION_INFRA]),
    loader: () =>
      import('./components/ArquitecturaContent').then(m => (
        <m.ArquitecturaContent />
      )),
  },
});

export const vantiPortalPlugin = createFrontendPlugin({
  pluginId: 'vanti-portal',
  extensions: [
    hojaDeVidaPage,
    integracionesPage,
    catalogoServiciosPage,
    usuariosPage,
    saludPage,
    hojaDeVidaCard,
    hojaDeVidaContent,
    pipelinesContent,
    variablesEntornoContent,
    arquitecturaContent,
  ],
  routes: {
    hojaDeVida: hojaDeVidaRouteRef,
    integraciones: integracionesRouteRef,
    catalogoServicios: catalogoServiciosRouteRef,
    usuarios: usuariosRouteRef,
    salud: saludRouteRef,
  },
});
